/*
 * <tt-cart-recos> — cart cross-sell that stays relevant and live.
 *
 * Relevance is anchored on the cart's DOMINANT card:
 *   - Every cart line's creature (Pokemon name parsed from the title) is tallied
 *     weighted by quantity. The majority creature wins (ties → first item).
 *     e.g. 2x Charizard + 1x Pikachu → "Charizard".
 *   1. CREATURE  — the majority creature → that creature across every set.
 *   2. SET       — the `twin_traders.set` metafield of a majority-creature card
 *                  → the rest of that set, used to top the row up.
 * Both come from /search/suggest.json. Results are merged creature-first,
 * de-duped, sold-out dropped (when settings.tt_hide_oos is on), anything
 * already in the cart removed, and capped at the configured count.
 *
 * The per-line cart data is passed in a <script data-tt-recos-seed> JSON block
 * (titles can't be escaped cleanly into attributes).
 *
 * Speed: cards paint as soon as the search resolves (one round-trip). The
 * search has no variant ids, so each Add button is wired up a beat later once
 * a cached/looked-up /products/<handle>.js resolves the variant.
 *
 * Staying live:
 *   - connectedCallback() reads the element's own seed, so the drawer
 *     (re-rendered wholesale on add / quantity change) re-initialises itself.
 *   - On the cart page the section isn't re-rendered on cart change, so the
 *     grid variant subscribes to cartUpdate and pulls a fresh seed via the
 *     Section Rendering API.
 */
/* ───────────────────────────────────────────────────────────────
   "Continue shopping" memory — remember the last game section the
   shopper browsed (Pokemon / Yu-Gi-Oh / Magic) and point the cart's
   Continue shopping link(s) back to it. Runs on every page, since this
   file loads globally via the cart drawer.
   ─────────────────────────────────────────────────────────────── */
(function () {
  var GAMES = [
    { re: /yu-?gi-?oh|yugioh/i, url: '/collections/yugioh-singles' },
    { re: /magic|mtg/i, url: '/collections/mtg-singles' },
    { re: /pok[eé]mon/i, url: '/collections/pokemon-singles' }
  ];
  function detectGameUrl() {
    var path;
    try { path = decodeURIComponent(location.pathname); } catch (e) { path = location.pathname; }
    var hay = path;
    // Product pages carry the game authoritatively in [data-game]; elsewhere use the path.
    if (/^\/products\//.test(path)) {
      var el = document.querySelector('[data-game]');
      if (el && el.getAttribute('data-game')) hay = el.getAttribute('data-game');
    }
    for (var i = 0; i < GAMES.length; i++) if (GAMES[i].re.test(hay)) return GAMES[i].url;
    return null;
  }
  try {
    var here = detectGameUrl();
    if (here) sessionStorage.setItem('tt-last-game-url', here);

    var links = document.querySelectorAll('[data-tt-continue]');
    if (links.length) {
      var saved = sessionStorage.getItem('tt-last-game-url');
      if (saved) links.forEach(function (a) { a.setAttribute('href', saved); });
    }
  } catch (e) { /* sessionStorage may be unavailable (private mode) — leave defaults */ }
})();

if (!customElements.get('tt-cart-recos')) {
  // Page-lifetime cache of handle → variant info, so re-renders don't re-fetch.
  var ttVariantCache = {};

  // Words that are NOT part of a card's identity (printing / set / grade / edition /
  // boilerplate) — stripped when building the dedup key so different printings of the
  // same card collapse and owned cards are excluded across every printing.
  var TT_KEY_NOISE = {};
  ('pokemon yugioh yu gi oh card cards common uncommon rare super ultra secret ghost starlight gold holo holofoil reverse foil starfoil parallel prismatic mosaic stamped unlimited unl limited 1st first edition editions ed mixed varying sealed promo near mint nm lp mp hp dmg light play moderate heavy played damaged excellent good gem poor condition japanese english german french italian spanish korean asian of the with and a an to for in on set sets alternate alt art full complete reprint reprinted starter deck structure collection collectors collector tin box pack misprint error oversized jumbo proxy')
    .split(' ').forEach(function (w) { TT_KEY_NOISE[w] = true; });

  class TtCartRecos extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-tt-recos-list]');
      this.layout = this.dataset.ttLayout || 'grid';
      this.render();

      // The cart page doesn't re-render this section on cart change → listen.
      // The drawer is re-rendered server-side, so it doesn't need to.
      if (this.layout === 'grid' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refresh.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    creatureOf(title) {
      var m = (title || '').match(/^\s*Pok[eé]mon Card\s+([A-Za-z0-9.'’-]+)/i);
      return m ? m[1] : '';
    }

    isPokemon(title) {
      return /^\s*Pok[eé]mon Card\s+/i.test(title || '');
    }

    // Meaningful card-name words for Yu-Gi-Oh / Magic titles — the set code, quantity,
    // rarity, edition and condition stripped off the messy
    // "<SETCODE> <Name> <rarity> <edition> <condition> Yugioh Card" format.
    nameWords(title) {
      var t = (title || '').replace(/^\s+/, '');
      t = t.replace(/^[A-Z0-9]+(?:-[A-Z0-9]+)?\s+/, ' '); // leading set code (DB1-EN137, LC02…)
      t = ' ' + t + ' ';
      t = t.replace(/\bx\d+\b/gi, ' ');                   // quantities like x3
      var STOP = /^(?:common|uncommon|rare|unlimited|unl|limited|1st|first|edition|editions|mixed|varying|sealed|promo|set|mint|nm|lp|mp|hp|dmg|damaged|excellent|condition|played|poor|gem|holo|holofoil|foil|starfoil|parallel|prismatic|mosaic|japanese|english|german|french|italian|spanish|korean|asian|yugioh|card|cards)$/i;
      var SKIP = /^(?:of|the|with|and|a|an|to|for|in|on)$/i;
      var out = [], words = t.split(/\s+/);
      for (var i = 0; i < words.length; i++) {
        var w = words[i].replace(/[^A-Za-z0-9'’-]/g, '');
        if (!/[A-Za-z0-9]/.test(w)) continue; // skip pure punctuation (stray hyphens)
        if (STOP.test(w)) break;              // hit rarity/condition → name ended
        if (SKIP.test(w)) continue;           // skip connector words
        out.push(w);
      }
      return out;
    }

    // Majority-grouping key: Pokemon creature, or the 2-word Yu-Gi-Oh / Magic archetype.
    subjectOf(title) {
      if (this.isPokemon(title)) return this.creatureOf(title);
      return this.nameWords(title).slice(0, 2).join(' ');
    }

    // Search queries for a card. Pokemon → [creature]. Yu-Gi-Oh / Magic → the name
    // archetype from BOTH ends (first 2 + last 2 words), so a shared family token like
    // "Forbidden One" is found even when it sits at the end of the name (Exodia's
    // limbs all share it). Searching both ends surfaces related cards, not just reprints.
    subjectQueries(title) {
      if (this.isPokemon(title)) { var c = this.creatureOf(title); return c ? [c] : []; }
      var w = this.nameWords(title);
      if (!w.length) return [];
      var first = w.slice(0, 2).join(' '), last = w.slice(-2).join(' ');
      return (last && last !== first) ? [first, last] : [first];
    }

    // The game's singles collection handle for a card title — used to top the row up
    // with in-stock cards from the same game when relevant matches run short.
    collectionOf(title) {
      var t = title || '';
      if (/yu-?gi-?oh|yugioh/i.test(t)) return 'yugioh-singles';
      if (/\bmtg\b|magic/i.test(t)) return 'mtg-singles';
      if (/pok[eé]mon/i.test(t)) return 'pokemon-singles';
      return '';
    }

    // Identity of a card ignoring its printing / set / grade — so different printings
    // of the same card collapse (dedup) and owned cards are excluded across all printings.
    cardKey(title) {
      var t = (title || '').replace(/^\s+/, '');
      t = t.replace(/^[A-Z0-9]{2,}(?:-[A-Z0-9]+)?\s+/, ' '); // leading Yu-Gi-Oh set code
      t = (' ' + t + ' ').toLowerCase().replace(/\bx\d+\b/g, ' ');
      var out = [], words = t.replace(/[^a-z0-9]+/g, ' ').split(/\s+/);
      for (var i = 0; i < words.length; i++) {
        if (words[i] && !TT_KEY_NOISE[words[i]]) out.push(words[i]);
      }
      return out.join(' ');
    }

    // Build the search seed from a host element's per-line cart JSON.
    computeSeed(el) {
      var self = this;
      var items = [];
      try {
        var tag = el.querySelector('[data-tt-recos-seed]');
        if (tag) items = JSON.parse(tag.textContent) || [];
      } catch (e) { items = []; }

      // Tally by number of distinct cards (line items) per subject → majority
      // (quantity ignored; ties keep first-seen order). The subject is the Pokemon
      // creature, or — for Yu-Gi-Oh / Magic — the card-name archetype.
      var counts = {}, order = [];
      items.forEach(function (it) {
        var c = self.subjectOf(it.title);
        if (!c) return;
        var key = c.toLowerCase();
        if (!(key in counts)) { counts[key] = 0; order.push({ key: key, name: c }); }
        counts[key] += 1;
      });
      var subject = '', best = 0;
      order.forEach(function (o) {
        if (counts[o.key] > best) { best = counts[o.key]; subject = o.name; }
      });

      // Representative item for that subject (else the first item) — its name drives
      // the search queries and its set drives the top-up.
      var rep = items.length ? items[0] : null;
      if (subject) {
        for (var i = 0; i < items.length; i++) {
          if (self.subjectOf(items[i].title).toLowerCase() === subject.toLowerCase()) { rep = items[i]; break; }
        }
      }

      return {
        queries: rep ? self.subjectQueries(rep.title) : [],
        set: ((rep && rep.set) || (items[0] && items[0].set) || '').trim(),
        collection: self.collectionOf((rep && rep.title) || (items[0] && items[0].title) || ''),
        cartKeys: items.map(function (it) { return self.cardKey(it.title); }),
        ids: items.map(function (it) { return String(it.id); }).filter(Boolean),
        hideOos: el.dataset.ttHideOos === '1',
        limit: parseInt(el.dataset.ttLimit, 10) || 6,
      };
    }

    suggest(query, hideOos) {
      if (!query) return Promise.resolve([]);
      var url = '/search/suggest.json?q=' + encodeURIComponent(query)
        + '&resources[type]=product&resources[limit]=10'
        + '&resources[options][unavailable_products]=' + (hideOos ? 'hide' : 'last');
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) { return d ? ((((d.resources || {}).results || {}).products) || []) : []; })
        .catch(function () { return []; });
    }

    render(sourceEl) {
      if (!this.list) return;
      var s = this.computeSeed(sourceEl || this);
      // Nothing to anchor on AND nowhere to top up from → genuinely empty.
      if (!s.queries.length && !s.set && !s.collection) { this.hide(); return; }
      var self = this;
      var token = (this.seq = (this.seq || 0) + 1); // guards against an older render painting over a newer one

      // Search each name query (similar names) in order, then the set (same-set top-up).
      var searches = s.queries.map(function (q) { return self.suggest(q, s.hideOos); });
      if (s.set) searches.push(self.suggest(s.set, s.hideOos));

      Promise.all(searches).then(function (lists) {
        if (self.seq !== token) return;

        var exclIds = {}; s.ids.forEach(function (id) { exclIds[id] = true; });
        var exclKeys = {}; s.cartKeys.forEach(function (k) { if (k) exclKeys[k] = true; });
        var pickedKeys = {};
        var picked = [];

        function take(list) {
          for (var i = 0; i < list.length && picked.length < s.limit; i++) {
            var p = list[i];
            if (!p || exclIds[p.id]) continue;
            if (s.hideOos && p.available === false) continue;
            var k = self.cardKey(p.title);
            // skip cards already in the cart (any printing) + duplicate printings already picked
            if (k && (exclKeys[k] || pickedKeys[k])) continue;
            if (k) pickedKeys[k] = true;
            picked.push(p);
          }
        }

        lists.forEach(take);

        // Relevant matches go first; if they ran short, top the row up with other
        // in-stock cards from the same game so it always fills to the limit.
        if (picked.length >= s.limit) { self.paint(picked, token); return; }
        self.topUp(s).then(function (extra) {
          if (self.seq !== token) return;
          take(extra);
          self.paint(picked, token);
        });
      });
    }

    // Top-up source: the same game's singles collection (in-stock, not relevance-ranked),
    // mapped to the same shape as /search/suggest.json results so take()/card() reuse it.
    topUp(s) {
      if (!s.collection) return Promise.resolve([]);
      return fetch('/collections/' + s.collection + '/products.json?limit=100')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var prods = (d && d.products) || [];
          return prods.map(function (p) {
            var vs = p.variants || [];
            var avail = false;
            for (var i = 0; i < vs.length; i++) { if (vs[i].available) { avail = true; break; } }
            return {
              id: p.id,
              title: p.title,
              handle: p.handle,
              url: '/products/' + p.handle,
              available: avail,
              price: (vs[0] && vs[0].price) || '',
              featured_image: { url: (p.images && p.images[0] && p.images[0].src) || '' }
            };
          });
        })
        .catch(function () { return []; });
    }

    // Render the picked cards and arm their Add buttons as each variant resolves.
    paint(picked, token) {
      var self = this;
      if (!picked.length) { self.hide(); return; }

      // Paint immediately — Add buttons start disabled and arm as variants land.
      self.list.innerHTML = picked.map(function (p, i) { return self.card(p, i); }).join('');
      self.hidden = false;

      picked.forEach(function (p, i) {
        self.withVariant(p).then(function (pp) {
          if (self.seq !== token) return; // a newer render replaced these cards
          var btn = self.list.querySelector('[data-tt-recos-add="' + i + '"]');
          if (!btn) return;
          btn.classList.remove('is-loading');
          if (pp.variantId && pp.variantAvailable !== false) {
            btn.dataset.ttQuickadd = pp.variantId;
            if (pp.max != null) btn.dataset.ttMax = pp.max;
            btn.disabled = false;
          }
        });
      });
    }

    // Resolve the product's variant (singles have one) for quick-add; cached per handle.
    withVariant(p) {
      var cached = ttVariantCache[p.handle];
      if (cached) {
        p.variantId = cached.variantId;
        p.variantAvailable = cached.available;
        if (cached.max != null) p.max = cached.max;
        return Promise.resolve(p);
      }
      return fetch('/products/' + p.handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          if (prod && prod.variants && prod.variants.length) {
            var v = null;
            for (var i = 0; i < prod.variants.length; i++) {
              if (prod.variants[i].available) { v = prod.variants[i]; break; }
            }
            if (!v) v = prod.variants[0];
            p.variantId = v.id;
            p.variantAvailable = v.available;
            if (v.inventory_management && v.inventory_policy === 'deny') p.max = v.inventory_quantity;
            ttVariantCache[p.handle] = { variantId: p.variantId, available: p.variantAvailable, max: p.max };
          }
          return p;
        })
        .catch(function () { return p; });
    }

    // Cart page only: cart changed but our section wasn't re-rendered, so fetch
    // a fresh seed for the new cart and render from it.
    refresh() {
      var self = this;
      var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
      fetch(cartUrl + '?section_id=cart-recommendations')
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (text) {
          if (text == null) return;
          var fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-tt-cart-recos]');
          if (!fresh) { self.hide(); return; } // cart emptied
          self.render(fresh);
        })
        .catch(function () {});
    }

    hide() {
      this.hidden = true;
      if (this.list) this.list.innerHTML = '';
    }

    money(v) {
      var n = parseFloat(v);
      return isNaN(n) ? '' : '£' + n.toFixed(2);
    }

    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    card(p, i) {
      var img = (p.featured_image && p.featured_image.url) || p.image || '';
      var url = this.esc(p.url);
      var ttl = this.esc(p.title);
      var price = this.money(p.price);
      var available = p.available !== false; // from search; the variant lookup confirms it
      var loading = available ? ' is-loading' : '';

      if (this.layout === 'drawer') {
        return ''
          + '<li class="tt-drawer-recos__card' + (available ? '' : ' is-sold-out') + '">'
          +   '<a href="' + url + '" class="tt-drawer-recos__media" aria-label="' + ttl + '">'
          +     (img ? '<img src="' + this.esc(img) + '" alt="' + ttl + '" loading="lazy" width="100" height="140">' : '')
          +     (available ? '' : '<span class="tt-drawer-recos__sold">Sold out</span>')
          +   '</a>'
          +   '<a href="' + url + '" class="tt-drawer-recos__title">' + ttl + '</a>'
          +   '<button type="button" class="tt-drawer-recos__add' + loading + '" data-tt-recos-add="' + i + '" disabled aria-label="Add ' + ttl + ' to basket">'
          +     '<span class="tt-drawer-recos__add-plus" aria-hidden="true">+</span>'
          +     '<span class="tt-drawer-recos__add-price">' + price + '</span>'
          +   '</button>'
          + '</li>';
      }

      // grid layout (cart page) — View link + quick-add, like the collection cards
      return ''
        + '<li class="grid__item"><article class="tt-grid-card' + (available ? '' : ' tt-grid-card--sold-out') + '">'
        +   '<div class="tt-grid-card__media">'
        +     '<a class="tt-grid-card__image-link" href="' + url + '" aria-label="' + ttl + '">'
        +       (img
                  ? '<img class="tt-grid-card__img tt-grid-card__img--front" src="' + this.esc(img) + '" alt="' + ttl + '" loading="lazy">'
                  : '<div class="tt-grid-card__placeholder"></div>')
        +     '</a>'
        +     (available ? '' : '<span class="tt-grid-card__sold-out">Sold out</span>')
        +   '</div>'
        +   '<div class="tt-grid-card__info">'
        +     '<a href="' + url + '" class="tt-grid-card__title">' + ttl + '</a>'
        +     '<div class="tt-grid-card__price">' + price + '</div>'
        +     '<div class="tt-grid-card__actions">'
        +       '<a class="tt-grid-card__btn tt-grid-card__btn--view" href="' + url + '" aria-label="View ' + ttl + '">'
        +         '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>'
        +         '<span>View</span>'
        +       '</a>'
        +       '<button type="button" class="tt-grid-card__btn tt-grid-card__btn--add' + loading + '" data-tt-recos-add="' + i + '" disabled aria-label="Add to cart: ' + ttl + '">'
        +         '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5.5 8h13l.9 11.2a1 1 0 0 1-1 1.1H5.6a1 1 0 0 1-1-1.1L5.5 8Z"/><path d="M9 8a3 3 0 0 1 6 0"/><path d="M12 12v4M10 14h4"/></svg>'
        +         '<span>' + (available ? 'Add' : 'Sold') + '</span>'
        +       '</button>'
        +     '</div>'
        +   '</div>'
        + '</article></li>';
    }
  }

  customElements.define('tt-cart-recos', TtCartRecos);
}
